import { IconsEnum } from '../../../../assets/icons/types';
import { Icon } from '../../../../helpers/Icon';
import { RefButtonHelper } from '../../../../helpers/buttons/RefButtonHelper';
import { TooltipDropdownHelper } from '../../../../helpers/dropdowns/TooltipDropdownHelper';
import { PopoverPlacement } from '../../../../helpers/Popover/popoverConstants';
import { Translate } from '../../../../helpers/Translate';
import { composerKeys } from '../../../../locales/keys';
import type { ComposerBackgroundMode } from '../../hooks/useComposerBackground';

interface ComposerBackgroundButtonProps {
  backgroundMode: ComposerBackgroundMode;
  customColor: string;
  onSelectPreset: (mode: 'default' | 'black' | 'white' | 'gray') => void;
  onSelectCustom: () => void;
}

const itemClass =
  'flex w-full items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700';

function ComposerBackgroundButton({
  backgroundMode,
  customColor,
  onSelectPreset,
  onSelectCustom
}: ComposerBackgroundButtonProps) {
  return (
    <div className="relative inline-flex">
      <TooltipDropdownHelper
        icon={IconsEnum.COLOR_BUCKET_SOLID}
        iconClassName="h-5 w-5"
        buttonClassName="py-2 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
        dropdownPlacement={PopoverPlacement.TOP_START}
        popoverClassName="min-w-44 z-50 overflow-y-auto bg-white dark:bg-gray-800 border border-transparent focus:outline-none py-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700"
        tooltipText="Background color"
        closeOnOuterClick
      >
        <RefButtonHelper
          className={itemClass}
          onClick={() => onSelectPreset('default')}
        >
          <Translate id={composerKeys.backgroundDefault} />
          {backgroundMode === 'default' && (
            <Icon
              className="h-4 w-4 text-blue-600"
              icon={IconsEnum.CHECK_OUTLINE}
            />
          )}
        </RefButtonHelper>

        <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

        <RefButtonHelper
          className={itemClass}
          onClick={() => onSelectPreset('black')}
        >
          <Translate id={composerKeys.backgroundBlack} />
          {backgroundMode === 'black' && (
            <Icon
              className="h-4 w-4 text-blue-600"
              icon={IconsEnum.CHECK_OUTLINE}
            />
          )}
        </RefButtonHelper>

        <RefButtonHelper
          className={itemClass}
          onClick={() => onSelectPreset('white')}
        >
          <Translate id={composerKeys.backgroundWhite} />
          {backgroundMode === 'white' && (
            <Icon
              className="h-4 w-4 text-blue-600"
              icon={IconsEnum.CHECK_OUTLINE}
            />
          )}
        </RefButtonHelper>

        <RefButtonHelper
          className={itemClass}
          onClick={() => onSelectPreset('gray')}
        >
          <Translate id={composerKeys.backgroundGray} />
          {backgroundMode === 'gray' && (
            <Icon
              className="h-4 w-4 text-blue-600"
              icon={IconsEnum.CHECK_OUTLINE}
            />
          )}
        </RefButtonHelper>

        <RefButtonHelper className={itemClass} onClick={onSelectCustom}>
          <Translate id={composerKeys.backgroundCustom} />
          {backgroundMode === 'custom' && (
            <Icon
              className="h-4 w-4 text-blue-600"
              icon={IconsEnum.CHECK_OUTLINE}
            />
          )}
        </RefButtonHelper>
      </TooltipDropdownHelper>

      {backgroundMode === 'custom' && (
        <span
          className="absolute bottom-1 right-1 w-2.5 h-2.5 rounded-full border border-white dark:border-gray-800 pointer-events-none z-10"
          style={{ backgroundColor: customColor }}
        />
      )}
    </div>
  );
}

export default ComposerBackgroundButton;
